/* jshint maxcomplexity: 5, maxparams: 3, maxdepth: 2, maxstatements: 10 */
/**
 * This collection is a container of all the information related to prices.
 * It fetches the price breakdown data along with price display config from the server with fetchData()
 * in the initial state.
 *
 */
(function() {
    'use strict';

    const PriceBreakdownItem = require('../models/pricemanager.model');
    const SeatMapSelectionCollection = require('../collections/seatmap_preferences_selections.collection');
    const PB_ITEM_TYPE = require('../enums/pricebreakdown_item_type.enum');
    const PB_ITEM_ATTRS = require('../enums/pricebreakdown_item_attribute.enum');
    const dependencies = {
        'seatMapSelectionCollection': SeatMapSelectionCollection,
    };
    const ancillaryTypes = [
        PB_ITEM_TYPE.BAGGAGES,
        PB_ITEM_TYPE.BAGGAGES_WEIGHT,
        PB_ITEM_TYPE.INSURANCE,
        PB_ITEM_TYPE.INSURANCE_IN_CART,
        PB_ITEM_TYPE.CANX_INSURANCE,
        PB_ITEM_TYPE.ASSISTANCE_INSURANCE,
        PB_ITEM_TYPE.ADDITION,
        PB_ITEM_TYPE.SEAT,
        PB_ITEM_TYPE.SEATMAP,
        PB_ITEM_TYPE.SERVICE_OPTIONS,
        PB_ITEM_TYPE.REBOOKING_INSURANCE,
        PB_ITEM_TYPE.MEMBERSHIP_SUBSCRIPTION,
        PB_ITEM_TYPE.HAND_BAGGAGE,
        PB_ITEM_TYPE.FAST_TRACK,
    ];
    const paxTypes = [PB_ITEM_TYPE.ADULTS, PB_ITEM_TYPE.CHILDREN, PB_ITEM_TYPE.INFANTS];
    const CHANGE_EVENT = 'priceCollection.change';
    const ShoppingCart = require('../cart/cart');
    const parseCurrencyConfig = function(data) {
        this.currencyConfig.providerCurrency = data.providerCurrency;
        this.currencyConfig.realProviderCurrency = data.realProviderCurrency;
    };
    const parseBestPriceData = function(data) {
        this.bestPricePerPassenger = data.bestPricePerPassenger;
        this.bestPricePerPassengerFormatted = Odigeo.Utils.formatCurrency(this.bestPricePerPassenger);
    };
    const parsePromocode = function(data) {
        Odigeo._.each(data.items, function(item) {
            if (item.type === PB_ITEM_TYPE.PROMO_CODE) {
                item.quantity = 1;
                item.onePrice = -Math.abs(item.allPrice);
                item.allPrice = -Math.abs(item.allPrice);
            }
        });
    };
    const parseDynpackDiscount = function(data) {
        this.addItem(new PriceBreakdownItem({
            type: PB_ITEM_TYPE.DYNPACK_DISCOUNT_FEE,
            allPrice: data.dynpackFareDiscount || 0,
        }));
    };
    const setRefundableTaxes = function(data) {
        this.refundableTaxes = data.refundableTaxes;
    };

    const parseInsuranceInCart = function(data) {
        Odigeo._.each(data.items, function(item) {
            if (item.type === PB_ITEM_TYPE.INSURANCE_IN_CART) {
                item.type = PB_ITEM_TYPE.INSURANCE;
                item.conceptKey = item.conceptKey.replace(/-/g, '');
            }
        });
    };

    const parsePreselectedRefundableFareUpgradePrice = function(data) {
        this.preselectedRefundableFareUpgradePrice = data.preselectedRefundableFareUpgradePrice;
    };

    const parseAreAllPassengersWithBaggage = function(data) {
        this.areAllPassengersWithBaggage = data.areAllPassengersWithBaggage;
    };

    const parseBaggageBundleDiscountPercentage = function(data) {
        this.baggageBundleDiscountPercentage = data.baggageBundleDiscountPercentage;
    };

    const buildMembershipObj = function(membershipPerksDTO) {
        return {
            fee: membershipPerksDTO.fee,
            isMembershipPriceDisplay: true,
            isIncludedInPrice: membershipPerksDTO.includedInPrice,
            isMemberLoggedIn: membershipPerksDTO.memberLoggedIn,
            isAutoApplyDiscount: membershipPerksDTO.autoApplyDiscount,
        };
    };
    const parseMembershipPerksDTO = function(data) {
        const membershipPerksDTO = data.membershipPerksDTO;
        this.membershipPerks = membershipPerksDTO ? buildMembershipObj(membershipPerksDTO) : {};
    };
    const bindDependencies = function() {
        Odigeo._.each(dependencies, function(dependency, key) {
            this[key] = dependency.getInstance();
            this.listenTo(this[key], 'add remove update', updateItemsFromCollection.bind(this));
        }, this);
    };
    const updateItemsFromCollection = function(model, collection, options) {
        const pBItem = collection.getPriceBreakdownItem();
        Odigeo.Utils.isMatch(options, {add: true}) ? this.addItem(pBItem) : this.removeItem(pBItem);
    };
    const formatCurrency = function(qty, currency) {
        if (!Odigeo._.isUndefined(qty)) {
            return Odigeo.Utils.formatCurrency(qty, currency);
        }
    };

    const updateItemsFromShoppingCart = (items) => {
        items = items ? (items.items || items) : [];
        const cartItems = ShoppingCart.changedPolicies;
        for (let item of items) {
            const added = Odigeo._.get(cartItems.added, item.conceptKey, null);
            const cartItem = added || Odigeo._.get(cartItems.removed, item.conceptKey, null);
            if (cartItem) {
                const quantity = added ? 1 : 0;
                item.quantity = quantity;
                item.conceptLabel = cartItem.concept;
                item.id = cartItem.policy || cartItem.type || cartItem.id;
                if (cartItem.category === ShoppingCart.categories.INSURANCE) {
                    item.type = cartItem.extra.insuranceType;
                }
                const totalPrice = cartItem.price.total;
                item.onePrice = totalPrice / quantity;
                item.allPrice = totalPrice;
            }
        }
        return items;
    };

    const PriceBreakdownCollectionClass = Odigeo.Collection.extend({
        numPassengers: 0,
        allPaxHasTax: false,
        anyHasTax: false,
        anyHasFare: false,
        totalTicketPrice: 0,
        totalTicketProviderPrice: 0,
        totalTicketPricePerPax: 0,
        totalFarePricePerPax: 0,
        totalProviderFarePricePerPax: 0,
        totalTicketProviderPricePerPax: 0,
        totalTaxPricePerPax: 0,
        serviceFeeItemPrice: 0,
        serviceFeeItemPriceProvider: 0,
        areAllPassengersWithBaggage: 0,
        baggageBundleDiscountPercentage: 0,
        refundableTaxes: 0,
        bestPricePerPassenger: 0,
        currencyConfig: {
            providerCurrency: null,
            realProviderCurrency: null,
        },
        doesNotMatchCurrency: false,
        seatMapSelectionCollection: null,
        model: PriceBreakdownItem,
        membershipPerks: {},
        sortingOrder: [
            PB_ITEM_TYPE.ADULTS, PB_ITEM_TYPE.CHILDREN, PB_ITEM_TYPE.INFANTS,
            PB_ITEM_TYPE.SERVICE_FEE, PB_ITEM_TYPE.CC_FEE,
            PB_ITEM_TYPE.BAGGAGES, PB_ITEM_TYPE.BAGGAGES_WEIGHT,
            PB_ITEM_TYPE.SEAT, PB_ITEM_TYPE.SEATMAP,
            PB_ITEM_TYPE.HAND_BAGGAGE,
            PB_ITEM_TYPE.INSURANCE, PB_ITEM_TYPE.INSURANCE_IN_CART,
            PB_ITEM_TYPE.ASSISTANCE_INSURANCE,
            PB_ITEM_TYPE.REBOOKING_INSURANCE,
            PB_ITEM_TYPE.CANX_INSURANCE,
            PB_ITEM_TYPE.PROMO_CODE,
            PB_ITEM_TYPE.SERVICE_OPTIONS,
        ],
        isLoadedPromise: null,

        initialize() {
            this.isLoadedPromise = $.Deferred();
            bindDependencies.call(this);
        },

        comparator(model) {
            return Odigeo._.indexOf(this.sortingOrder, model.get(PB_ITEM_ATTRS.TYPE));
        },

        url() {
            return Odigeo.Settings.property('serviceUrlFormatter')('booking/pricebreakdown');
        },

        parse(data) {
            if (Odigeo._.isUndefined(data))
                return;
            parseCurrencyConfig.call(this, data);
            parseBestPriceData.call(this, data);
            parsePromocode.call(this, data);
            setRefundableTaxes.call(this, data);
            parseMembershipPerksDTO.call(this, data);
            parseInsuranceInCart.call(this, data);
            parsePreselectedRefundableFareUpgradePrice.call(this, data);
            parseAreAllPassengersWithBaggage.call(this, data);
            parseBaggageBundleDiscountPercentage.call(this, data);
            parseDynpackDiscount.call(this, data);

            return this.filterByQuantity(updateItemsFromShoppingCart(data));
        },

        fetchData() {
            this.fetch({reset: true}).always(() => {
                this.trigger('fetch:complete');
                this.resolvePromise(true);
                this.storeInmutableData();
                this.triggerChange();
                ShoppingCart.refresh();
            });
        },

        resolvePromise(isSuccessful) {
            this.isLoadedPromise.resolve(isSuccessful);
        },

        setMembershipPerksIncludedPrice(value) {
            this.membershipPerks.isIncludedInPrice = value;
        },

        filterByQuantity(items) {
            return Odigeo._.filter(items, function(item) {
                return item.quantity > 0;
            });
        },

        storeInmutableData() {
            this.numPassengers = this.calculatePassengersNum();
            this.allPaxHasTax = this.allPaxShouldHaveTax();
            this.anyHasTax = this.hasTax(PB_ITEM_TYPE.ADULTS);
            this.anyHasFare = this.hasFare(PB_ITEM_TYPE.ADULTS);
            this.totalTicketPrice = this.calculateTotalTicketPrice();
            this.totalTicketProviderPrice = this.calculateTotalTicketProviderPrice();
            this.totalTicketPricePerPax = !Odigeo._.isUndefined(this.numPassengers) && this.numPassengers !== 0 ?
                this.totalTicketPrice / this.numPassengers : 0;
            this.totalTicketProviderPricePerPax = !Odigeo._.isUndefined(this.numPassengers) && this.numPassengers !== 0 ?
                this.totalTicketProviderPrice / this.numPassengers : 0;
            this.serviceFeeItemPrice = this.getPriceBreakdownItemByType(PB_ITEM_TYPE.SERVICE_FEE).get(PB_ITEM_ATTRS.ALL_PRICE);
            this.serviceFeeItemPriceProvider = this.getPriceBreakdownItemByType(PB_ITEM_TYPE.SERVICE_FEE).get(PB_ITEM_ATTRS.MARKUP);
            this.totalFarePricePerPax = this.calculateTotalFarePricePerPax();
            this.totalProviderFarePricePerPax = this.calculateTotalProviderFarePricePerPax();
            this.totalTaxPricePerPax = this.calculateTotalTaxPricePerPax();
            this.doesNotMatchCurrency = this.currencyConfig.providerCurrency !== this.currencyConfig.realProviderCurrency;
        },

        containsType(arg) {
            return this.some(function(model) {
                const matchType = Odigeo._.isArray(arg) ? model.matchTypeFromList(arg) : model.isMatchingType(arg);

                return matchType && model.isQuantifiable();
            });
        },

        getPriceBreakdownItemByType(type) {
            return this.findWhere({type: type}) || new this.model({type: type}); // eslint-disable-line new-cap
        },

        getPriceBreakdownItemsByType(type) {
            return this.where({type: type});
        },

        getPriceBreakdownItemByConceptKey(conceptKey) {
            return this.findWhere({conceptKey: Odigeo._.isNumber(conceptKey) ? conceptKey.toString() : conceptKey});
        },

        getPriceBreakdownItemByPolicy(policyVar) {
            return this.findWhere({policy: policyVar});
        },

        getPriceBreakdownItemByPolicyOrId(value) {
            const matches = this.models.filter((model) => value === model.get('policy') || value === model.get('id'));
            return matches ? matches[0] : null;
        },

        getPassengersNum() {
            return this.numPassengers;
        },

        allPaxShouldHaveTax() {
            return Odigeo._.every(paxTypes, function(type) {
                return this.hasTax(type);
            }, this);
        },

        hasTax(type) {
            const items = this.getPriceBreakdownItemsByType(type);
            return Odigeo._.some(items, function(item) {
                return item.hasPrice(PB_ITEM_ATTRS.TAX);
            });
        },

        hasFare(type) {
            const items = this.getPriceBreakdownItemsByType(type);
            return Odigeo._.some(items, function(item) {
                return item.hasPrice(PB_ITEM_ATTRS.FARE);
            });
        },

        isPaymentMethodCreditCard() {
            const method = this.getPriceBreakdownItemByType(PB_ITEM_TYPE.CC_FEE).get('method');
            return method === 'CREDITCARD' || method === 'SECURE3D';
        },

        addItem(priceBreakdownItem) {
            const model = Odigeo._.get(priceBreakdownItem, 'allowMultipleOfTheSameType', false) ?
                this.addMultipleOfTheSameTypeAllowed(priceBreakdownItem) :
                this.addNoMultipleOfTheSameTypeAllowed(priceBreakdownItem);

            if (model instanceof Odigeo.Model) {
                model.on('change', this.triggerChange);
            }

            this.triggerChange();

            return model;
        },

        addNoMultipleOfTheSameTypeAllowed(priceBreakdownItem) {
            const match = this.getPriceBreakdownItemByType(priceBreakdownItem.type);
            return this.replaceItemByIndex(match, priceBreakdownItem);
        },

        addMultipleOfTheSameTypeAllowed(priceBreakdownItem) {
            const itemExists = this.findItemWithSamePolicy(priceBreakdownItem);
            return !itemExists ? this.add(priceBreakdownItem) : itemExists;
        },

        findItemWithSamePolicy(priceBreakdownItem) {
            return Odigeo._.find(this.models, (item) => this.getAdditionOrInsurancePolicyPath(item) === priceBreakdownItem.policy);
        },

        getAdditionOrInsurancePolicyPath(item) {
            return item.attributes.policy || item.attributes.id;
        },

        removeItem(model) {
            if (model instanceof Odigeo.Model) {
                model.off('change', this.triggerChange);
            }

            this.remove(model);

            this.triggerChange();
        },

        replaceItemByIndex(oldModel, newModel) {
            const index = this.indexOf(oldModel);
            this.remove(this.at(index));
            return this.add(newModel, {at: index});
        },

        removeItemByType(type) {
            const priceBreakdownItem = this.getPriceBreakdownItemByType(type);
            this.remove(priceBreakdownItem);
            this.triggerChange();
        },

        getSHPrice() {
            const serviceFeeAllPrice = this.serviceFeeItemPrice || 0;
            const ccFeeAllPrice = this.getPriceBreakdownItemByType(PB_ITEM_TYPE.CC_FEE).get(PB_ITEM_ATTRS.ALL_PRICE) || 0;

            return formatCurrency((serviceFeeAllPrice + ccFeeAllPrice) / this.numPassengers);
        },

        calculatePassengersNum() {
            const totalPax = function(memo, model) {
                return memo + model.get(PB_ITEM_ATTRS.QUANTITY);
            };

            return this.calculationTotalPax(totalPax);
        },

        getTotalTicketPrice() {
            return +this.totalTicketPricePerPax.toFixed(2) * this.numPassengers;
        },

        calculateTotalPrice() {
            const total = this.getTotalTicketPrice();
            return this.reduce(function(memo, model) {
                if (this.appliesToTotal(model)) {
                    let price = model.getPrice();
                    if (model.isBaggageOrBaggageWeightItem()
                        && this.baggageBundleDiscountPercentage > 0
                        && this.areAllPassengersWithBaggage
                        && Odigeo.AB.isBaggageDiscountEnabled) {
                        price = price - ((this.baggageBundleDiscountPercentage / 100) * price);
                    }
                    return memo + price;
                }

                return memo;
            }, total, this);
        },

        appliesToTotal(model) {
            return !model.isPassengerItem() || (model.isInsuranceItem());
        },

        getTotalTicketPricePerPax() {
            return this.getTotalTicketPrice() / this.getPassengersNum();
        },

        calculatePricePerPassenger() {
            return this.calculateTotalPrice() / this.getPassengersNum();
        },

        calculationTotalPax(func) {
            if (!Odigeo._.isFunction(func)) {
                return;
            }

            return this.reduce(function(memo, model) {
                if (model.isPassengerItem()) {
                    return func(memo, model);
                }
                return memo;
            }, 0);
        },

        calculationPerPax(func) {
            if (!Odigeo._.isFunction(func)) {
                return;
            }

            return this.calculationTotalPax(func) / this.getPassengersNum();
        },

        calculateTotalTicketPrice() {
            const totalTicketCalculation = function(memo, model) {
                return memo + model.getPrice();
            };

            return this.calculationTotalPax(totalTicketCalculation);
        },

        calculateTotalTicketProviderPrice() {
            const totalTicketCalculation = function(memo, model) {
                return memo + model.getProviderPrice();
            };

            return this.calculationTotalPax(totalTicketCalculation);
        },

        calculateTotalFarePricePerPax() {
            const fareCalculation = function(memo, model) {
                return memo + model.get(PB_ITEM_ATTRS.FARE) * model.get(PB_ITEM_ATTRS.QUANTITY);
            };

            return this.calculationPerPax(fareCalculation);
        },

        calculateTotalProviderFarePricePerPax() {
            const fareCalculation = function(memo, model) {
                return memo + model.get(PB_ITEM_ATTRS.PROVIDER_FARE) * model.get(PB_ITEM_ATTRS.QUANTITY);
            };

            return this.calculationPerPax(fareCalculation);
        },

        calculateTotalTaxPricePerPax() {
            const taxCalculation = function(memo, model) {
                return memo + model.get(PB_ITEM_ATTRS.TAX) * model.get(PB_ITEM_ATTRS.QUANTITY);
            };

            return this.calculationPerPax(taxCalculation);
        },

        getPayWithGuaranteeItemInCollection() {
            const guaranteeItemType = PB_ITEM_TYPE.CANX_INSURANCE;
            return this.getPriceBreakdownItemByType(guaranteeItemType);
        },

        triggerChange() {
            this.trigger(CHANGE_EVENT);
        },

        filterPreselected(policyToFilter) {
            if (policyToFilter) {
                const modelsToRemove = this.filter((model) => model.get('id') === policyToFilter);
                for (let modelToRemove of modelsToRemove) {
                    this.removeItem(modelToRemove);
                }
                this.storeInmutableData();
                this.triggerChange();
            }
        },

        getJSONData() {
            return {
                items: this.toJSON(),
                numPassengers: this.numPassengers,
                bestPricePerPassenger: this.bestPricePerPassenger,
                bestPricePerPassengerFormatted: this.bestPricePerPassengerFormatted,
                currencyConfig: this.currencyConfig,
                doesNotMatchCurrency: this.doesNotMatchCurrency,
                hasBags: this.containsType([PB_ITEM_TYPE.BAGGAGES, PB_ITEM_TYPE.BAGGAGES_WEIGHT]),
                hasInsurances: this.containsType([
                    PB_ITEM_TYPE.INSURANCE_IN_CART,
                    PB_ITEM_TYPE.INSURANCE,
                    PB_ITEM_TYPE.CANX_INSURANCE,
                    PB_ITEM_TYPE.ASSISTANCE_INSURANCE,
                ]),
                hasSeats: this.containsType([PB_ITEM_TYPE.SEAT, PB_ITEM_TYPE.SEATMAP]),
                hasAncillaries: this.containsType(ancillaryTypes),
                hasDiscountFee: this.containsType(PB_ITEM_TYPE.DISCOUNT_FEE),
                hasInsuranceSelected: this.containsType([
                    PB_ITEM_TYPE.INSURANCE,
                    PB_ITEM_TYPE.ASSISTANCE_INSURANCE,
                    PB_ITEM_TYPE.INSURANCE_IN_CART,
                ]),
                allPaxHasTax: this.allPaxHasTax,
                hasTax: this.anyHasTax,
                hasFare: this.anyHasFare,
                totalPrice: this.calculateTotalPrice(),
                totalPricePerPax: this.calculatePricePerPassenger(),
                totalTicketPrice: this.totalTicketPrice,
                totalTicketPricePerPax: this.totalTicketPricePerPax,
                totalTicketProviderPricePerPax: this.totalTicketProviderPricePerPax,
                totalFarePricePerPax: formatCurrency(this.totalFarePricePerPax),
                totalProviderFarePricePerPax: formatCurrency(this.totalProviderFarePricePerPax),
                totalTaxPricePerPax: formatCurrency(this.totalTaxPricePerPax),
                totalFee: this.serviceFeeItemPrice + this.getPriceBreakdownItemByType(PB_ITEM_TYPE.CC_FEE).get(PB_ITEM_ATTRS.ALL_PRICE),
                totalFeeFormatted: formatCurrency(this.serviceFeeItemPrice +
                    this.getPriceBreakdownItemByType(PB_ITEM_TYPE.CC_FEE).get(PB_ITEM_ATTRS.ALL_PRICE)
                ),
                totalFeeProvider: this.serviceFeeItemPriceProvider,
                totalFeeProviderFormatted: formatCurrency(this.serviceFeeItemPriceProvider ),
                serviceFeeItem: this.getPriceBreakdownItemByType(PB_ITEM_TYPE.SERVICE_FEE).toJSON(),
                discountFeeItem: this.getPriceBreakdownItemByType(PB_ITEM_TYPE.DISCOUNT_FEE).toJSON(),
                dynpackDiscountFee: this.getPriceBreakdownItemByType(PB_ITEM_TYPE.DYNPACK_DISCOUNT_FEE).get(PB_ITEM_ATTRS.ALL_PRICE),
                ccFeeItem: this.getPriceBreakdownItemByType(PB_ITEM_TYPE.CC_FEE).toJSON(),
                ccFeeClass: this.getPriceBreakdownItemByType(PB_ITEM_TYPE.CC_FEE).get(PB_ITEM_ATTRS.ONE_PRICE) > 0 ? 'positive' : 'negative',
                methodIsCc: this.isPaymentMethodCreditCard(),
                refundableTaxes: this.refundableTaxes,
                refundableTaxesFormatted: formatCurrency(this.refundableTaxes),
                membershipPerks: this.membershipPerks,
                preselectedRefundableFareUpgradePrice: this.preselectedRefundableFareUpgradePrice,
                baggageBundleDiscountPercentage: this.baggageBundleDiscountPercentage,
                areAllPassengersWithBaggage: this.areAllPassengersWithBaggage,
            };
        },

        getLoadedPromise() {
            return this.isLoadedPromise.promise();
        },
    });
    let instance;

    // Singleton Pattern for this collection
    module.exports.getInstance = function() {
        if (Odigeo._.isUndefined(instance)) {
            instance = new PriceBreakdownCollectionClass();
        }

        return instance;
    };
})();
